/**
 * Tiny logging facade.
 *
 * - In dev / when no DSN: logs to console (warn / error / info / debug)
 * - When `process.env.REACT_APP_SENTRY_DSN` is set, the same calls also forward
 *   to Sentry via `Sentry.captureMessage` / `Sentry.captureException`.
 * - Sentry is loaded LAZILY so the bundle stays slim until you opt in.
 */

let sentry = null;
let sentryReady = false;

async function loadSentry() {
  if (sentryReady) return sentry;
  sentryReady = true;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return null;
  try {
    const mod = await import("@sentry/react");
    mod.init({
      dsn,
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACE_RATE || 0.1),
      environment: process.env.NODE_ENV || "production",
    });
    sentry = mod;
    return sentry;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[logger] Sentry init failed; falling back to console only:", e?.message || e);
    return null;
  }
}

// Kick off lazy load on first import (non-blocking).
loadSentry();

const fwd = (level, args) => {
  if (!sentry) return;
  const msg = args.map((a) => (a instanceof Error ? a : typeof a === "string" ? a : JSON.stringify(a))).join(" ");
  if (level === "error") {
    const err = args.find((a) => a instanceof Error);
    if (err) sentry.captureException(err, { level });
    else sentry.captureMessage(msg, level);
  } else {
    sentry.captureMessage(msg, level);
  }
};

export const logger = {
  debug: (...args) => {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV !== "production") console.debug(...args);
    fwd("debug", args);
  },
  info: (...args) => {
    // eslint-disable-next-line no-console
    console.info(...args);
    fwd("info", args);
  },
  warn: (...args) => {
    // eslint-disable-next-line no-console
    console.warn(...args);
    fwd("warning", args);
  },
  error: (...args) => {
    // eslint-disable-next-line no-console
    console.error(...args);
    fwd("error", args);
  },
};
